import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8090/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use((config) => {
  const tokenKey = process.env.REACT_APP_TOKEN_KEY || 'client_token';
  const token = localStorage.getItem(tokenKey);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface ClientFolder {
  id: string;
  red_rock_id: string;
  sharefile_folder_id?: string;
  sharefile_folder_url?: string;
  box_folder_id?: string;
  folder_name?: string;
  created: string;
  updated: string;
}

export interface ClientFolderResponse {
  items: ClientFolder[];
  page: number;
  perPage: number;
  totalItems: number;
  totalPages: number;
}

class ClientFolderService {
  /**
   * Get client folder information by red_rock_id
   */
  async getClientFolder(redRockId: string): Promise<ClientFolder | null> {
    try {
      console.log(`🔍 Fetching client folder for red_rock_id: ${redRockId}`);
      
      const response = await api.get(`/collections/client_folders/records?filter=(red_rock_id='${redRockId}')`);
      const data: ClientFolderResponse = response.data;
      
      if (data.items && data.items.length > 0) {
        const folder = data.items[0];
        console.log(`✅ Found client folder:`, folder);
        return folder;
      }
      
      console.log(`ℹ️ No client folder found for red_rock_id: ${redRockId}`);
      return null;
      
    } catch (error: any) {
      console.error('❌ Error fetching client folder:', error);
      throw new Error(`Failed to fetch client folder: ${error.message}`);
    }
  }

  /**
   * Get client folder information by record ID
   */
  async getClientFolderById(folderId: string): Promise<ClientFolder | null> {
    try {
      console.log(`🔍 Fetching client folder by ID: ${folderId}`);
      
      const response = await api.get(`/collections/client_folders/records/${folderId}`);
      const folder: ClientFolder = response.data;
      
      console.log(`✅ Found client folder:`, folder);
      return folder;
      
    } catch (error: any) {
      console.error('❌ Error fetching client folder by ID:', error);
      throw new Error(`Failed to fetch client folder: ${error.message}`);
    }
  }

  /**
   * Get all client folders for a user (if they have access to multiple)
   */
  async getClientFolders(redRockIds: string[]): Promise<ClientFolder[]> {
    try {
      if (redRockIds.length === 0) {
        return [];
      }

      console.log(`🔍 Fetching client folders for ${redRockIds.length} red_rock_ids`);
      
      // Create filter for multiple red_rock_ids
      const filter = redRockIds.map(id => `red_rock_id='${id}'`).join(' || ');
      
      const response = await api.get(`/collections/client_folders/records?filter=(${filter})`);
      const data: ClientFolderResponse = response.data;
      
      console.log(`✅ Found ${data.items.length} client folders`);
      return data.items;
      
    } catch (error: any) {
      console.error('❌ Error fetching client folders:', error);
      throw new Error(`Failed to fetch client folders: ${error.message}`);
    }
  }

  /**
   * Check if a client has a ShareFile folder configured
   */
  async hasShareFileFolder(redRockId: string): Promise<boolean> {
    try {
      const folder = await this.getClientFolder(redRockId);
      return folder !== null && 
             folder.sharefile_folder_id !== undefined && 
             folder.sharefile_folder_id !== null && 
             folder.sharefile_folder_id !== '';
    } catch (error) {
      console.error('❌ Error checking ShareFile folder:', error);
      return false;
    }
  }

  /**
   * Get ShareFile folder ID for a client
   */
  async getShareFileFolderId(redRockId: string): Promise<string | null> {
    try {
      const folder = await this.getClientFolder(redRockId);
      return folder?.sharefile_folder_id || null;
    } catch (error) {
      console.error('❌ Error getting ShareFile folder ID:', error);
      return null;
    }
  }

  /**
   * Get ShareFile folder URL for a client
   */
  async getShareFileFolderUrl(redRockId: string): Promise<string | null> {
    try {
      const folder = await this.getClientFolder(redRockId);
      return folder?.sharefile_folder_url || null;
    } catch (error) {
      console.error('❌ Error getting ShareFile folder URL:', error);
      return null;
    }
  }
}

const clientFolderService = new ClientFolderService();
export default clientFolderService;

